import React, { useState } from 'react'
import {
  Box,
  Heading,
  Text,
  Button,
  VStack,
  HStack,
  Container,
  SimpleGrid,
  Stack,
  Badge,
  Flex,
  Link,
  Image,
} from '@chakra-ui/react'
import { useNavigate, Link as RouterLink } from 'react-router-dom'
import { buildPaidPlanCtaUrl, buildRegistrationUrl, type BillingInterval } from '../utils/billingCheckout'
import { plans as planConfigs, getPlanPrice, formatPlanPrice } from '../config/pricing'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../contexts/AuthContext'
import {
  FiUpload,
  FiBarChart2,
  FiMessageCircle,
  FiStar,
  FiCheck,
  FiActivity,
  FiShield,
  FiUsers,
  FiTrendingUp,
  FiArrowRight,
  FiZap,
  FiLock,
  FiChevronLeft,
  FiChevronRight,
  FiFileText,
} from 'react-icons/fi'
import { PublicHeader } from '../components/Layout/PublicHeader'
import { PublicFooter } from '../components/Layout/PublicFooter'
import { FeatureTextWithTooltip } from '../components/Account/FeatureTextWithTooltip'
import { scenarioCategorySlug } from '../data/scenarios'
import {
  MOBILE_APP_LANDING_ASSETS,
  MOBILE_APP_STORE_URLS,
} from '../config/mobileAppStores'

// Simple public landing page. Extend or replace with real marketing later.
export const HomePage: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { currentUser } = useAuth()
  const [billingCycle, setBillingCycle] = useState<BillingInterval>('monthly')
  const [currentTestimonial, setCurrentTestimonial] = useState(0)

  const basicMonthly = planConfigs.find(p => p.id === 'basic')?.priceValue ?? 12.95
  const premiumMonthly = planConfigs.find(p => p.id === 'premium')?.priceValue ?? 25

  const billingCycleHintKey: Record<BillingInterval, string> = {
    monthly: 'home.billing_cycle_monthly_hint',
    quarterly: 'home.billing_cycle_quarterly_hint',
    yearly: 'home.billing_cycle_yearly_hint',
  }

  const getPaidPlanDisplay = (plan: 'basic' | 'premium') => {
    const monthly = plan === 'basic' ? basicMonthly : premiumMonthly
    const planPrice = { priceValue: monthly }
    const payOnceNote = t('home.pay_once_note', {
      price: formatPlanPrice(getPlanPrice(planPrice, 'quarterly')),
    })
    if (billingCycle === 'yearly') {
      return {
        price: formatPlanPrice(getPlanPrice(planPrice, 'yearly')),
        cadence: t('home.per_year'),
        priceNote: payOnceNote,
      }
    }
    if (billingCycle === 'quarterly') {
      return {
        price: formatPlanPrice(getPlanPrice(planPrice, 'quarterly')),
        cadence: t('home.per_3_months'),
        priceNote: t('home.quarterly_price_note'),
      }
    }
    return {
      price: formatPlanPrice(monthly),
      cadence: t('home.per_month'),
      priceNote: payOnceNote,
    }
  }
  const basicPricing = getPaidPlanDisplay('basic')
  const premiumPricing = getPaidPlanDisplay('premium')
  
  return (
    <Box>
      {/* Modern Marketing Navbar */}
      <PublicHeader />

      {/* Modern Hero Section */}
      <Box
        position="relative"
        overflow="hidden"
        bg="bg"
        pt="72px"
      >
        {/* Background Gradient */}
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          bgGradient="linear(to-br, brand.50, bg, accent.50)"
          _dark={{ bgGradient: 'linear(to-br, brand.900, bg, accent.900)' }}
          opacity={0.6}
          zIndex={0}
        />
        
        {/* Decorative Elements */}
        <Box
          position="absolute"
          top="10%"
          right="5%"
          w="400px"
          h="400px"
          bg="brand.100"
          rounded="full"
          filter="blur(100px)"
          opacity={0.3}
          zIndex={0}
        />
        <Box
          position="absolute"
          bottom="20%"
          left="0%"
          w="350px"
          h="350px"
          bg="accent.100"
          rounded="full"
          filter="blur(100px)"
          opacity={0.3}
          zIndex={0}
        />

        <Container maxW="7xl" position="relative" zIndex={1}>
          <Stack 
            direction={{ base: 'column', lg: 'row' }} 
            align="center" 
            gap={{ base: 12, lg: 16 }}
            py={{ base: 16, md: 20, lg: 24 }}
            minH={{ base: 'auto', lg: 'calc(100vh - 72px)' }}
          >
            {/* Left Content */}
            <VStack 
              align={{ base: 'center', lg: 'start' }} 
              flex={1} 
              gap={8} 
              textAlign={{ base: 'center', lg: 'left' }}
              maxW={{ base: 'full', lg: '600px' }}
            >
              {/* Badge */}
              <HStack gap={2} flexWrap="wrap" justify={{ base: 'center', lg: 'start' }}>
                <Badge
                  px={4}
                  py={2}
                  rounded="full"
                  fontSize="sm"
                  fontWeight="600"
                  display="inline-flex"
                  alignItems="center"
                  gap={2}
                  bg="brand.50"
                  color="brand.700"
                  border="1px solid"
                  borderColor="brand.200"
                >
                  <FiZap size="14px" />
                  {t('home.hero_badge')}
                </Badge>
                <Badge
                  colorScheme="green"
                  variant="subtle"
                  px={3}
                  py={2}
                  rounded="full"
                  fontSize="sm"
                  fontWeight="600"
                >
                  Start with your data
                </Badge>
              </HStack>

              {/* Main Heading */}
              <Heading
                as="h1"
                fontFamily="alteHaas"
                fontWeight="bold"
                fontSize={{ base: '4xl', md: '5xl', lg: '6xl' }}
                lineHeight="1.1"
                letterSpacing="-0.02em"
                color="fg"
              >
                {t('home.hero_title')}{' '}
                <Text as="span" color="brand.500">
                  {t('home.hero_title_highlight')}
                </Text>
              </Heading>

              {/* Subtitle */}
              <Text
                fontSize={{ base: 'lg', md: 'xl' }}
                color="fg.muted"
                lineHeight="1.7"
                maxW="540px"
              >
                {t('home.hero_subtitle')}
              </Text>

              {/* Example prompts */}
              <VStack
                align={{ base: 'center', lg: 'start' }}
                gap={2}
                w="full"
                maxW="500px"
              >
                {[
                  'Upload my genetics and health records',
                  'Ask what my results mean',
                  'Turn the findings into wellness goals',
                ].map((prompt) => (
                  <HStack
                    key={prompt}
                    gap={3}
                    px={4}
                    py={2.5}
                    bg="bg.muted"
                    border="1px solid"
                    borderColor="border"
                    rounded="lg"
                    w="full"
                  >
                    <FiMessageCircle size="14px" color="var(--chakra-colors-brand-500)" style={{ flexShrink: 0 }} />
                    <Text fontSize="sm" color="fg.muted" fontStyle="italic">"{prompt}"</Text>
                  </HStack>
                ))}
              </VStack>

              {/* CTA Buttons */}
              <HStack
                gap={4}
                flexWrap="wrap"
                justify={{ base: 'center', lg: 'start' }}
                pt={2}
              >
                <Button
                  asChild
                  size="lg"
                  px={8}
                  h="56px"
                  fontSize="md"
                  fontWeight="700"
                  bg="brand.500"
                  color="white"
                  _hover={{ bg: 'brand.600', transform: 'translateY(-2px)', shadow: 'md' }}
                  transition="all 0.2s"
                >
                  <RouterLink to={currentUser ? '/chat' : buildRegistrationUrl({ source: 'homepage-hero' })}>
                    {t('home.get_started_free')}
                    <FiArrowRight size="18px" style={{ marginLeft: '8px' }} />
                  </RouterLink>
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  px={8}
                  h="56px"
                  fontSize="md"
                  fontWeight="600"
                  borderWidth="2px"
                  _hover={{
                    bg: 'bg.muted',
                    transform: 'translateY(-2px)',
                    shadow: 'md'
                  }}
                  transition="all 0.2s"
                >
                  <RouterLink to="/register">
                    Create free account
                  </RouterLink>
                </Button>
              </HStack>

              {/* Trust Indicators */}
              <HStack
                gap={6}
                pt={2}
                flexWrap="wrap"
                justify={{ base: 'center', lg: 'start' }}
                color="fg.muted"
                fontSize="sm"
              >
                <HStack gap={2}>
                  <FiShield size="16px" color="var(--chakra-colors-brand-500)" />
                  <Text fontWeight="500">Privacy-first security</Text>
                </HStack>
                <HStack gap={2}>
                  <FiLock size="16px" color="var(--chakra-colors-brand-500)" />
                  <Text fontWeight="500">Your data stays yours</Text>
                </HStack>
                <HStack gap={2}>
                  <FiZap size="16px" color="var(--chakra-colors-brand-500)" />
                  <Text fontWeight="500">Built around your health context</Text>
                </HStack>
              </HStack>
            </VStack>

            {/* Right Content — Mock health chat conversation */}
            <Box
              flex={1}
              maxW={{ base: 'full', lg: '540px' }}
              w="full"
              position="relative"
            >
              {/* Window chrome */}
              <Box
                rounded="2xl"
                overflow="hidden"
                shadow="xl"
                border="1px solid"
                borderColor="gray.200"
                transition="all 0.3s ease"
                _hover={{ shadow: '2xl' }}
              >
                {/* Title bar */}
                <Box bg="gray.900" px={4} py={3}>
                  <HStack gap={2} mb={2}>
                    <Box w="10px" h="10px" rounded="full" bg="red.400" />
                    <Box w="10px" h="10px" rounded="full" bg="yellow.400" />
                    <Box w="10px" h="10px" rounded="full" bg="green.400" />
                    <Text fontSize="xs" color="gray.400" ml={2} fontFamily="mono">LiveWello health chat — Your context</Text>
                  </HStack>
                </Box>

                {/* Messages */}
                <VStack gap={0} align="stretch" bg="gray.50">
                  {/* User message */}
                  <Flex justify="flex-end" px={4} py={3}>
                    <Box
                      bg="gray.800"
                      color="white"
                      px={4}
                      py={2.5}
                      rounded="2xl"
                      roundedBottomRight="sm"
                      maxW="80%"
                      fontSize="sm"
                      lineHeight="1.5"
                    >
                      I uploaded my genetic file and recent labs. What should I understand first?
                    </Box>
                  </Flex>

                  {/* Context pill */}
                  <Box px={4} pb={1}>
                    <HStack
                      gap={2}
                      px={3}
                      py={1.5}
                      bg="brand.50"
                      border="1px solid"
                      borderColor="brand.200"
                      rounded="lg"
                      display="inline-flex"
                    >
                      <FiZap size="11px" color="var(--chakra-colors-brand-600)" />
                      <Text fontSize="xs" color="brand.700" fontFamily="mono">Reviews genetics, labs, and goals</Text>
                    </HStack>
                  </Box>

                  {/* Health chat reply */}
                  <Box px={4} py={3}>
                    <Box
                      bg="white"
                      border="1px solid"
                      borderColor="gray.200"
                      px={4}
                      py={3}
                      rounded="2xl"
                      roundedBottomLeft="sm"
                      maxW="92%"
                      fontSize="sm"
                      lineHeight="1.7"
                      color="gray.800"
                      shadow="xs"
                    >
                      Your MTHFR result suggests you may process folate less efficiently. Your B12 is also near the low end of your recent labs, so this is worth discussing with your clinician before changing supplements.
                    </Box>
                  </Box>

                  {/* Second exchange */}
                  <Flex justify="flex-end" px={4} py={2}>
                    <Box
                      bg="gray.800"
                      color="white"
                      px={4}
                      py={2.5}
                      rounded="2xl"
                      roundedBottomRight="sm"
                      maxW="80%"
                      fontSize="sm"
                      lineHeight="1.5"
                    >
                      Can you turn that into a practical goal for this week?
                    </Box>
                  </Flex>

                  <Box px={4} pb={1}>
                    <HStack
                      gap={2}
                      px={3}
                      py={1.5}
                      bg="brand.50"
                      border="1px solid"
                      borderColor="brand.200"
                      rounded="lg"
                      display="inline-flex"
                    >
                      <FiZap size="11px" color="var(--chakra-colors-brand-600)" />
                      <Text fontSize="xs" color="brand.700" fontFamily="mono">Creates a wellness goal in LiveWello</Text>
                    </HStack>
                  </Box>

                  <Box px={4} pt={2} pb={4}>
                    <Box
                      bg="white"
                      border="1px solid"
                      borderColor="gray.200"
                      px={4}
                      py={3}
                      rounded="2xl"
                      roundedBottomLeft="sm"
                      maxW="92%"
                      fontSize="sm"
                      lineHeight="1.7"
                      color="gray.800"
                      shadow="xs"
                    >
                      Start with a simple goal: track folate-rich meals for 7 days and add notes on energy. I can remind you, watch your progress, and help you prepare questions for your next appointment.
                    </Box>
                  </Box>
                </VStack>

                {/* Input bar */}
                <Box bg="white" px={4} py={3} borderTop="1px solid" borderColor="gray.100">
                  <Box
                    border="1px solid"
                    borderColor="gray.200"
                    rounded="xl"
                    px={4}
                    py={2.5}
                    fontSize="sm"
                    color="gray.400"
                  >
                    Ask about your health data...
                  </Box>
                </Box>
              </Box>

              {/* Floating "powered by" badge */}
              <Box
                position="absolute"
                bottom="-16px"
                right="16px"
                bg="white"
                rounded="xl"
                shadow="md"
                px={4}
                py={2.5}
                border="1px solid"
                borderColor="brand.200"
                display={{ base: 'none', lg: 'block' }}
              >
                <HStack gap={2}>
                  <Box w="8px" h="8px" rounded="full" bg="brand.500" />
                  <Text fontSize="xs" fontWeight="600" color="gray.600">
                    Powered by your LiveWello record
                  </Text>
                </HStack>
              </Box>
            </Box>
          </Stack>
        </Container>

        {/* Bottom Wave */}
        <Box 
          position="absolute" 
          bottom={-2} 
          left={0} 
          right={0}
          h="120px"
        >
          <svg 
            viewBox="0 0 1440 120" 
            fill="none" 
            xmlns="http://www.w3.org/2000/svg"
            style={{ width: '100%', height: '100%' }}
          >
            <path 
              d="M0,64L48,69.3C96,75,192,85,288,80C384,75,480,53,576,48C672,43,768,53,864,64C960,75,1056,85,1152,80C1248,75,1344,53,1392,42.7L1440,32L1440,120L1392,120C1344,120,1248,120,1152,120C1056,120,960,120,864,120C768,120,672,120,576,120C480,120,384,120,288,120C192,120,96,120,48,120L0,120Z" 
              fill="var(--chakra-colors-bg)"
            />
          </svg>
        </Box>
      </Box>

      {/* Features Section */}
      <Box id="features" py={{ base: 16, md: 24 }} bg="bg">
        <Container maxW="7xl">
          <VStack gap={16}>
            {/* Section Header */}
            <VStack gap={4} textAlign="center" maxW="700px">
              <Badge
                colorScheme="green"
                variant="subtle"
                px={3}
                py={1}
                rounded="full"
                fontSize="xs"
                fontWeight="600"
                textTransform="uppercase"
                letterSpacing="wide"
              >
                Features
              </Badge>
              <Heading 
                size="2xl" 
                color="fg"
                fontWeight="700"
                letterSpacing="-0.01em"
              >
                {t('home.features_title')}
              </Heading>
              <Text fontSize="lg" color="fg.muted" lineHeight="tall">
                {t('home.features_subtitle')}
              </Text>
            </VStack>

            {/* Feature Cards */}
            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} gap={6} w="full">
              {[{
                icon: FiBarChart2,
                title: t('home.feature_genetics_title'),
                description: t('home.feature_genetics_desc'),
                color: 'brand',
                showDnaLink: true
              },{
                icon: FiTrendingUp,
                title: t('home.feature_goals_title'),
                description: t('home.feature_goals_desc'),
                color: 'accent'
              },{
                icon: FiActivity,
                title: t('home.feature_interactions_title'),
                description: t('home.feature_interactions_desc'),
                color: 'brand'
              },{
                icon: FiShield,
                title: t('home.feature_privacy_title'),
                description: t('home.feature_privacy_desc'),
                color: 'accent'
              }              ].map((feature, index) => (
                <Box 
                  key={index} 
                  bg="bg" 
                  border="1px solid" 
                  borderColor="border" 
                  rounded="xl" 
                  p={6}
                  shadow="sm"
                  transition="all 0.3s ease"
                  _hover={{
                    shadow: 'md',
                    transform: 'translateY(-4px)',
                    borderColor: `${feature.color}.200`
                  }}
                  cursor="pointer"
                >
                  <VStack align="start" gap={4}>
                    <Box 
                      bg={`${feature.color}.50`}
                      _dark={{ bg: `${feature.color}.900` }}
                      rounded="lg" 
                      p={3}
                      display="inline-flex"
                    >
                      <feature.icon 
                        size="24px" 
                        color={`var(--chakra-colors-${feature.color}-600)`} 
                      />
                    </Box>
                    <Heading 
                      size="md" 
                      color="fg"
                      fontWeight="600"
                    >
                      {feature.title}
                    </Heading>
                    <Text 
                      color="fg.muted" 
                      fontSize="sm"
                      lineHeight="tall"
                    >
                      {feature.description}
                    </Text>
                    {feature.showDnaLink && (
                      <Button 
                        asChild 
                        size="sm" 
                        variant="ghost"
                        colorScheme="green"
                        mt={2}
                      >
                        <RouterLink to="/dna-kit">
                          {t('home.dna_kit_link', 'Need genetic data?')} <FiArrowRight size="14px" style={{ marginLeft: '4px' }} />
                        </RouterLink>
                      </Button>
                    )}
                  </VStack>
                </Box>
              ))}
            </SimpleGrid>
          </VStack>
        </Container>
      </Box>

      {/* Mobile App Section */}
      <Box id="mobile" py={{ base: 16, md: 24 }} bg="bg.muted" data-testid="mobile-app-section">
        <Container maxW="7xl">
          <Stack
            direction={{ base: 'column', lg: 'row' }}
            gap={{ base: 12, lg: 16 }}
            align={{ base: 'center', lg: 'center' }}
          >
            <VStack
              align={{ base: 'center', lg: 'start' }}
              gap={6}
              flex={1}
              maxW={{ base: '680px', lg: '520px' }}
              textAlign={{ base: 'center', lg: 'left' }}
            >
              <Badge
                colorScheme="green"
                variant="subtle"
                px={3}
                py={1}
                rounded="full"
                fontSize="xs"
                fontWeight="600"
                textTransform="uppercase"
                letterSpacing="wide"
              >
                {t('home.mobile_badge')}
              </Badge>
              <Heading size="2xl" color="fg" fontWeight="700" letterSpacing="-0.01em">
                {t('home.mobile_title')}
              </Heading>
              <Text fontSize="lg" color="fg.muted" lineHeight="tall">
                {t('home.mobile_subtitle')}
              </Text>
              <HStack
                gap={4}
                wrap="wrap"
                justify={{ base: 'center', lg: 'start' }}
                pt={2}
                data-testid="mobile-app-store-links"
              >
                <Link
                  href={MOBILE_APP_STORE_URLS.apple}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={t('home.mobile_ios_aria')}
                  display="inline-flex"
                  transition="transform 0.2s ease"
                  _hover={{ transform: 'translateY(-2px)' }}
                  data-testid="mobile-app-store-apple"
                >
                  <Image
                    src={MOBILE_APP_LANDING_ASSETS.appStoreBadge}
                    alt=""
                    h={{ base: '44px', md: '48px' }}
                    w="auto"
                  />
                </Link>
                <Link
                  href={MOBILE_APP_STORE_URLS.googlePlay}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={t('home.mobile_android_aria')}
                  display="inline-flex"
                  transition="transform 0.2s ease"
                  _hover={{ transform: 'translateY(-2px)' }}
                  data-testid="mobile-app-store-google"
                >
                  <Image
                    src={MOBILE_APP_LANDING_ASSETS.googlePlayBadge}
                    alt=""
                    h={{ base: '64px', md: '68px' }}
                    w="auto"
                  />
                </Link>
              </HStack>
            </VStack>

            <SimpleGrid
              columns={{ base: 1, sm: 2 }}
              gap={{ base: 8, md: 10 }}
              flex={1.2}
              w="full"
              maxW="760px"
              data-testid="mobile-app-screenshots"
            >
              {[
                {
                  platform: 'iPhone',
                  caption: t('home.mobile_genetics_caption'),
                  src: MOBILE_APP_LANDING_ASSETS.iosGeneticsScreenshot,
                  alt: 'Livewello iPhone app showing a personalized genetics report in Wellness Coach',
                  testId: 'mobile-app-screenshot-ios',
                  frameVariant: 'ios' as const,
                },
                {
                  platform: 'Android',
                  caption: t('home.mobile_goals_caption'),
                  src: MOBILE_APP_LANDING_ASSETS.androidGoalsScreenshot,
                  alt: 'Livewello Android app showing wellness goal check-ins and streak tracking',
                  testId: 'mobile-app-screenshot-android',
                  frameVariant: 'android' as const,
                },
              ].map((shot) => (
                <VStack key={shot.platform} gap={3} align="center">
                  <Box
                    position="relative"
                    w="full"
                    maxW="280px"
                    mx="auto"
                    data-testid={shot.testId}
                  >
                    <Box
                      bg={shot.frameVariant === 'ios' ? 'gray.900' : 'gray.800'}
                      rounded={shot.frameVariant === 'ios' ? '44px' : '32px'}
                      p={shot.frameVariant === 'ios' ? '10px' : '8px'}
                      shadow="2xl"
                      border="1px solid"
                      borderColor={shot.frameVariant === 'ios' ? 'gray.700' : 'gray.600'}
                    >
                      {shot.frameVariant === 'ios' && (
                        <Box
                          position="absolute"
                          top="18px"
                          left="50%"
                          transform="translateX(-50%)"
                          w="96px"
                          h="24px"
                          bg="gray.900"
                          rounded="full"
                          zIndex={2}
                          border="1px solid"
                          borderColor="gray.700"
                        />
                      )}
                      {shot.frameVariant === 'android' && (
                        <Box
                          position="absolute"
                          top="22px"
                          left="50%"
                          transform="translateX(-50%)"
                          w="12px"
                          h="12px"
                          bg="gray.900"
                          rounded="full"
                          zIndex={2}
                          border="1px solid"
                          borderColor="gray.600"
                        />
                      )}
                      <Box
                        overflow="hidden"
                        rounded={shot.frameVariant === 'ios' ? '34px' : '26px'}
                        bg="white"
                        aspectRatio="9/19.5"
                      >
                        <Image
                          src={shot.src}
                          alt={shot.alt}
                          w="full"
                          h="full"
                          objectFit="cover"
                          objectPosition="top"
                          loading="lazy"
                        />
                      </Box>
                    </Box>
                  </Box>
                  <Text fontSize="sm" color="fg.muted" textAlign="center" fontWeight="500">
                    {shot.caption}
                  </Text>
                </VStack>
              ))}
            </SimpleGrid>
          </Stack>
        </Container>
      </Box>

      {/* How It Works Section */}
      <Box id="how-it-works" py={20} bg="bg">
        <Container maxW="7xl">
          <VStack gap={16}>
            <VStack gap={4} textAlign="center" maxW="600px">
              <Heading size="2xl" color="fg" fontFamily="alteHaas" fontWeight="bold">
                {t('home.how_it_works')}
              </Heading>
              <Text fontSize="lg" color="fg.muted">
                {t('home.how_it_works_subtitle')}
              </Text>
            </VStack>

            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} gap={8}>
              {[
                {
                  icon: FiUpload,
                  title: t('home.step_1_title'),
                  description: t('home.step_1_desc'),
                  step: '01',
                  color: 'brand'
                },
                {
                  icon: FiFileText,
                  title: t('home.step_2_title'),
                  description: t('home.step_2_desc'),
                  step: '02',
                  color: 'accent'
                },
                {
                  icon: FiZap,
                  title: t('home.step_3_title'),
                  description: t('home.step_3_desc'),
                  step: '03',
                  color: 'brand'
                },
                {
                  icon: FiUsers,
                  title: t('home.step_4_title'),
                  description: t('home.step_4_desc'),
                  step: '04',
                  color: 'accent'
                },
              ].map((step, index) => (
                <Box 
                  key={index} 
                  bg="bg" 
                  border="1px solid" 
                  borderColor="border" 
                  rounded="xl" 
                  p={8} 
                  textAlign="center"
                  shadow="sm"
                  transition="all 0.3s ease"
                  _hover={{
                    shadow: 'md',
                    transform: 'translateY(-4px)',
                    borderColor: `${step.color}.200`
                  }}
                >
                  <VStack gap={4}>
                    <Badge 
                      colorScheme="green" 
                      variant="subtle" 
                      fontSize="sm" 
                      px={3} 
                      py={1}
                      rounded="full"
                      fontWeight="600"
                    >
                      {step.step}
                    </Badge>
                    <Box 
                      bg={`${step.color}.50`}
                      _dark={{ bg: `${step.color}.900` }}
                      rounded="full" 
                      p={4}
                      display="inline-flex"
                    >
                      <step.icon size="32px" color={`var(--chakra-colors-${step.color}-600)`} />
                    </Box>
                    <Heading size="md" color="fg" fontWeight="600">
                      {step.title}
                    </Heading>
                    <Text color="fg.muted" fontSize="sm" lineHeight="tall">
                      {step.description}
                    </Text>
                  </VStack>
                </Box>
              ))}
            </SimpleGrid>
            
            {/* Learn More Links */}
            <HStack justify="center" gap={4} wrap="wrap">
              <Button
                asChild
                size="lg"
                colorScheme="brand"
                variant="outline"
              >
                <RouterLink to="/how-to-use">
                  <HStack gap={2}>
                    <span>{t('home.how_it_works_learn_more', 'View All Use Cases & Detailed Guides')}</span>
                    <FiArrowRight />
                  </HStack>
                </RouterLink>
              </Button>
              <Button
                asChild
                size="lg"
                colorPalette="teal"
                variant="solid"
              >
                <RouterLink to="/tutorials">
                  <HStack gap={2}>
                    <span>Product tutorials with screenshots</span>
                    <FiArrowRight />
                  </HStack>
                </RouterLink>
              </Button>
            </HStack>
          </VStack>
        </Container>
      </Box>

      {/* See it in action — Scenario cases teaser */}
      <Box py={{ base: 16, md: 24 }} bg="bg.muted">
        <Container maxW="7xl">
          <VStack gap={12}>
            <VStack gap={4} textAlign="center" maxW="680px">
              <Badge
                colorScheme="purple"
                variant="subtle"
                px={3}
                py={1}
                rounded="full"
                fontSize="xs"
                fontWeight="600"
                textTransform="uppercase"
                letterSpacing="wide"
              >
                Real-world cases
              </Badge>
              <Heading size="2xl" color="fg" fontWeight="700" letterSpacing="-0.01em">
                See what it actually finds
              </Heading>
              <Text fontSize="lg" color="fg.muted" lineHeight="tall">
                These fictional-but-clinically-grounded cases show the kinds of insights that only
                emerge when genetics, health records, and guided analysis work together — the things standard
                blood work alone can miss.
              </Text>
            </VStack>

            <SimpleGrid columns={{ base: 1, md: 3 }} gap={6} w="full">
              {[
                {
                  series: 'Methylation & MTHFR',
                  title: 'The fatigue her "normal" blood test missed',
                  hook: "Maya's B12 was 215 — inside normal range. Her genetics explained why she still felt terrible.",
                  slug: 'methylation-maya',
                  color: 'purple',
                },
                {
                  series: 'APOE & Brain Health',
                  title: "Her mother's diagnosis changed everything",
                  hook: "Lena, 52, wanted to know her risk. Her genetics gave her a plan, not just a number.",
                  slug: 'apoe-lena',
                  color: 'blue',
                },
                {
                  series: 'Serotonin & Mood',
                  title: "Everything was fine. She still felt terrible.",
                  hook: "Priya's mood made no sense given her life. Her genetics revealed a nervous system wired to feel stress harder — and what to do about it.",
                  slug: 'serotonin-priya',
                  color: 'green',
                },
              ].map((card) => (
                <RouterLink
                  key={card.slug}
                  to={`/scenarios/${scenarioCategorySlug(card.series)}/${card.slug}`}
                  style={{ display: 'flex', textDecoration: 'none' }}
                >
                  <Box
                    bg="bg"
                    border="1px solid"
                    borderColor="border"
                    rounded="xl"
                    p={6}
                    shadow="sm"
                    display="flex"
                    flexDirection="column"
                    flex={1}
                    transition="all 0.2s ease"
                    cursor="pointer"
                    _hover={{
                      shadow: 'md',
                      transform: 'translateY(-3px)',
                      borderColor: `${card.color}.200`,
                    }}
                  >
                    <VStack align="start" gap={3} flex={1}>
                      <Badge colorPalette={card.color} variant="subtle" fontSize="xs">
                        {card.series}
                      </Badge>
                      <Heading size="sm" color="fg" lineHeight="snug">
                        {card.title}
                      </Heading>
                      <Text fontSize="sm" color="fg.muted" lineHeight="tall" flex={1}>
                        {card.hook}
                      </Text>
                    </VStack>
                    <HStack color="brand.500" fontSize="sm" fontWeight="semibold" mt={4}>
                      <Text>Read the case</Text>
                      <FiArrowRight size="14px" />
                    </HStack>
                  </Box>
                </RouterLink>
              ))}
            </SimpleGrid>

            <Button
              asChild
              size="lg"
              variant="outline"
              colorScheme="brand"
            >
              <RouterLink to="/scenarios">
                <HStack gap={2}>
                  <span>Browse all cases</span>
                  <FiArrowRight />
                </HStack>
              </RouterLink>
            </Button>
          </VStack>
        </Container>
      </Box>

      {/* Membership Plans Section */}
      <Box id="pricing" py={20} bgGradient="linear(to-b, bg.muted, bg)" _dark={{ bgGradient: 'linear(to-b, bg.muted, bg)' }}>
        <Container maxW="7xl">
          <VStack gap={16}>
            <VStack gap={4} textAlign="center" maxW="600px">
              <Heading size="2xl" color="fg" fontFamily="alteHaas" fontWeight="bold">
                {t('home.flexible_plans')}
              </Heading>
              <Text fontSize="lg" color="fg.muted">
                {t('home.flexible_plans_subtitle')}
              </Text>
              <VStack gap={2} mt={2} w="full" maxW="md" mx="auto">
                <HStack gap={2} justify="center" flexWrap="wrap">
                  {(['monthly', 'quarterly', 'yearly'] as const).map((cycle) => {
                    const isSelected = billingCycle === cycle
                    return (
                      <Button
                        key={cycle}
                        size="sm"
                        variant={isSelected ? 'solid' : 'outline'}
                        colorScheme="green"
                        color={isSelected ? 'white' : undefined}
                        onClick={() => setBillingCycle(cycle)}
                      >
                        {t(`home.billing_toggle_${cycle}`)}
                      </Button>
                    )
                  })}
                </HStack>
                <Text fontSize="sm" color="fg.muted" textAlign="center">
                  {t(billingCycleHintKey[billingCycle])}
                </Text>
              </VStack>
            </VStack>

            <SimpleGrid columns={{ base: 1, md: 3 }} gap={8} w="full">
              {/* Free Plan */}
              <Box bg="bg" border="2px solid" borderColor="border" rounded="lg" p={8} display="flex" flexDirection="column" height="100%">
                <VStack gap={6} align="stretch" flex="1">
                  <VStack gap={2} align="start">
                    <Heading size="lg" color="fg">
                      {t('home.free_plan')}
                    </Heading>
                    <Text color="fg.muted">{t('home.free_plan_subtitle')}</Text>
                  </VStack>
                  <HStack align="baseline">
                    <Heading size="2xl" color="fg">
                      $0
                    </Heading>
                    <Text color="fg.muted">
                      {billingCycle === 'yearly' ? t('home.per_year') : t('home.per_month')}
                    </Text>
                  </HStack>
                  <VStack align="start" gap={3}>
                    {[t('home.free_features.profiles'), t('home.free_features.tokens'), t('home.free_features.storage'), t('home.free_features.uploads'), t('home.free_features.reports'), t('home.free_features.health_records'), t('home.free_features.goals'), t('home.free_features.ai_coaching'), t('home.free_features.retention'), t('home.free_features.mcp_access')].map((feature, index) => (
                      <HStack key={index} align="center" gap={2}>
                        <FiCheck size="16px" color="var(--chakra-colors-brand-500)" />
                        <FeatureTextWithTooltip text={feature} fontSize="sm" />
                      </HStack>
                    ))}
                  </VStack>
                </VStack>
                <Button asChild variant="outline" size="lg" w="full" mt="auto">
                  <RouterLink to={buildRegistrationUrl({ source: 'homepage' })}>{t('home.get_started')}</RouterLink>
                </Button>
              </Box>

              {/* Basic Plan */}
              <Box bg="bg" border="2px solid" borderColor="brand.500" rounded="lg" p={8} position="relative" display="flex" flexDirection="column" height="100%" overflow="visible">
                {/* Prominent "Most Popular" badge */}
                <Badge
                  bg="green.500"
                  color="white"
                  position="absolute"
                  top={-3}
                  left="50%"
                  transform="translateX(-50%)"
                  px={4}
                  py={2}
                  fontSize="sm"
                  fontWeight="bold"
                  textTransform="uppercase"
                  letterSpacing="wide"
                  borderRadius="full"
                  boxShadow="lg"
                  zIndex={2}
                  border="2px solid"
                  borderColor="bg"
                >
                  {t('home.most_popular')}
                </Badge>
                <VStack gap={6} align="stretch" flex="1">
                  <VStack gap={2} align="start">
                    <Heading size="lg" color="fg">
                      {t('home.basic_plan')}
                    </Heading>
                    <Text color="fg.muted">{t('home.basic_plan_subtitle')}</Text>
                  </VStack>
                  <VStack align="start" gap={1}>
                    <HStack align="baseline">
                      <Heading size="2xl" color="fg">
                        {basicPricing.price}
                      </Heading>
                      <Text color="fg.muted">{basicPricing.cadence}</Text>
                    </HStack>
                    {basicPricing.priceNote ? (
                      <Text fontSize="sm" color="fg.muted">
                        {basicPricing.priceNote}
                      </Text>
                    ) : null}
                  </VStack>
                  <VStack align="start" gap={3}>
                    {[t('home.basic_features.profiles'), t('home.basic_features.tokens'), t('home.basic_features.storage'), t('home.basic_features.reports'), t('home.basic_features.custom_reports'), t('home.basic_features.goals'), t('home.basic_features.shared_profiles'), t('home.basic_features.mcp_access')].map((feature, index) => (
                      <HStack key={index} align="center" gap={2}>
                        <FiCheck size="16px" color="var(--chakra-colors-brand-500)" />
                        <FeatureTextWithTooltip text={feature} fontSize="sm" />
                      </HStack>
                    ))}
                  </VStack>
                </VStack>
                <Button asChild colorScheme="green" size="lg" w="full" color="white" mt="auto">
                  <RouterLink to={buildPaidPlanCtaUrl({ plan: 'basic', interval: billingCycle, source: 'homepage', isAuthenticated: !!currentUser })}>{t('home.choose_basic')}</RouterLink>
                </Button>
              </Box>

              {/* Premium Plan */}
              <Box bg="bg" border="2px solid" borderColor="border" rounded="lg" p={8} display="flex" flexDirection="column" height="100%">
                <VStack gap={6} align="stretch" flex="1">
                  <VStack gap={2} align="start">
                    <Heading size="lg" color="fg">
                      {t('home.premium_plan')}
                    </Heading>
                    <Text color="fg.muted">{t('home.premium_plan_subtitle')}</Text>
                  </VStack>
                  <VStack align="start" gap={1}>
                    <HStack align="baseline">
                      <Heading size="2xl" color="fg">
                        {premiumPricing.price}
                      </Heading>
                      <Text color="fg.muted">{premiumPricing.cadence}</Text>
                    </HStack>
                    {premiumPricing.priceNote ? (
                      <Text fontSize="sm" color="fg.muted">
                        {premiumPricing.priceNote}
                      </Text>
                    ) : null}
                  </VStack>
                   <VStack align="start" gap={3}>
                     {[
                       t('home.premium_features.all_basic'),
                       t('home.premium_features.profiles'),
                       t('home.premium_features.tokens'),
                       t('home.premium_features.storage'),
                       t('home.premium_features.reports'),
                       t('home.premium_features.custom_reports'),
                       t('home.premium_features.mcp_access'),
                     ].map((feature, index) => (
                      <HStack key={index} align="center" gap={2}>
                        <FiCheck size="16px" color="var(--chakra-colors-brand-500)" />
                        <FeatureTextWithTooltip text={feature} fontSize="sm" />
                      </HStack>
                    ))}
                  </VStack>
                </VStack>
                <Button asChild variant="solid" size="lg" w="full" colorScheme="green" color="white" mt="auto">
                  <RouterLink to={buildPaidPlanCtaUrl({ plan: 'premium', interval: billingCycle, source: 'homepage', isAuthenticated: !!currentUser })}>{t('home.start_premium')}</RouterLink>
                </Button>
              </Box>

            </SimpleGrid>
          </VStack>
        </Container>
      </Box>


      {/* Privacy & Compliance */}
      <Box id="privacy" py={20} bg="bg.muted">
        <Container maxW="7xl">
          <VStack gap={12}>
            <VStack gap={4} textAlign="center" maxW="700px">
              <Heading size="2xl" color="fg" fontFamily="alteHaas" fontWeight="bold">{t('home.privacy_title')}</Heading>
              <Text fontSize="lg" color="fg.muted">{t('home.privacy_subtitle')}</Text>
            </VStack>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={6} w="full">
              {[t('home.privacy_items.privacy_first'), t('home.privacy_items.gdpr'), t('home.privacy_items.encryption'), t('home.privacy_items.consent'), t('home.privacy_items.data_never_sold'), t('home.privacy_items.genetic_pledge')].map((item, index) => (
                <HStack key={index} bg="bg" border="1px solid" borderColor="border" rounded="lg" p={4}>
                  <FiShield size="18px" color="var(--chakra-colors-green-600)" />
                  <Text color="fg">{item}</Text>
                </HStack>
              ))}
            </SimpleGrid>
          </VStack>
        </Container>
      </Box>

      {/* Testimonials Section - Carousel */}
      <Box py={20} bg="bg">
        <Container maxW="5xl">
          <VStack gap={12}>
            <VStack gap={4} textAlign="center" maxW="600px">
              <Heading size="2xl" color="fg" fontFamily="alteHaas" fontWeight="bold">
                {t('home.testimonials_title')}
              </Heading>
              <Text fontSize="lg" color="fg.muted">
                {t('home.testimonials_subtitle')}
              </Text>
            </VStack>

            {/* Carousel Container */}
            <Box position="relative" w="full" overflow="hidden">
              {/* Left Arrow */}
              <Button
                position="absolute"
                left={{ base: 2, md: 4 }}
                top="50%"
                transform="translateY(-50%)"
                zIndex={2}
                onClick={() => setCurrentTestimonial((prev) => (prev === 0 ? 7 : prev - 1))}
                variant="solid"
                bg="bg"
                color="fg"
                rounded="full"
                w="48px"
                h="48px"
                minW="48px"
                p={0}
                shadow="md"
                _hover={{
                  bg: 'bg.muted',
                  transform: 'translateY(-50%) scale(1.05)',
                  shadow: 'lg'
                }}
                _active={{
                  transform: 'translateY(-50%) scale(0.95)'
                }}
                aria-label="Previous testimonial"
              >
                <FiChevronLeft size="24px" />
              </Button>

              {/* Right Arrow */}
              <Button
                position="absolute"
                right={{ base: 2, md: 4 }}
                top="50%"
                transform="translateY(-50%)"
                zIndex={2}
                onClick={() => setCurrentTestimonial((prev) => (prev === 7 ? 0 : prev + 1))}
                variant="solid"
                bg="bg"
                color="fg"
                rounded="full"
                w="48px"
                h="48px"
                minW="48px"
                p={0}
                shadow="md"
                _hover={{
                  bg: 'bg.muted',
                  transform: 'translateY(-50%) scale(1.05)',
                  shadow: 'lg'
                }}
                _active={{
                  transform: 'translateY(-50%) scale(0.95)'
                }}
                aria-label="Next testimonial"
              >
                <FiChevronRight size="24px" />
              </Button>

              <Box
                display="flex"
                transition="transform 0.5s ease-in-out"
                transform={`translateX(-${currentTestimonial * 100}%)`}
              >
                {[
                  {
                    quote: t('home.testimonial_1'),
                    name: t('home.testimonial_1_name'),
                    role: t('home.testimonial_1_role'),
                    bg: 'wellness.mint',
                    borderColor: 'brand.100',
                    avatarBg: 'brand.500'
                  },
                  {
                    quote: t('home.testimonial_2'),
                    name: t('home.testimonial_2_name'),
                    role: t('home.testimonial_2_role'),
                    bg: 'wellness.peach',
                    borderColor: 'accent.100',
                    avatarBg: 'accent.500'
                  },
                  {
                    quote: "Managing my mom's health records was overwhelming until I found LiveWello. Now I can organize her medications, appointments, and wellness goals all in one place. She doesn't need to learn anything technical—I handle it all for her.",
                    name: "Sarah Mitchell",
                    role: "Caregiver for Elderly Parent",
                    bg: 'wellness.lavender',
                    borderColor: 'brand.100',
                    avatarBg: 'brand.600'
                  },
                  {
                    quote: "As a health coach, the free collaboration is a game-changer. My clients don't need to pay for an account—they just share what's relevant with me. It makes working together so much easier.",
                    name: "Dr. James Patterson",
                    role: "Health Coach & Nutritionist",
                    bg: 'wellness.mint',
                    borderColor: 'accent.100',
                    avatarBg: 'accent.600'
                  },
                  {
                    quote: "My teenage son has special needs, and tracking his health information used to be scattered across paper files and emails. Now everything is organized, and I can easily share specific records with his therapists and doctors.",
                    name: "Jennifer Thompson",
                    role: "Parent of Child with Disabilities",
                    bg: 'wellness.peach',
                    borderColor: 'brand.100',
                    avatarBg: 'brand.400'
                  },
                  {
                    quote: "I manage wellness for my entire family—my husband, two kids, and my dad. Having all their health information in one system, but keeping it private for each person, is exactly what I needed.",
                    name: "Maria Rodriguez",
                    role: "Family Health Manager",
                    bg: 'wellness.cream',
                    borderColor: 'accent.100',
                    avatarBg: 'accent.500'
                  },
                  {
                    quote: "My wife helps me track my diabetes and medications since I'm not great with technology. She can see my health records and remind me about my goals, but she didn't have to create an account or pay anything.",
                    name: "Robert Chen",
                    role: "Managing Chronic Condition",
                    bg: 'wellness.mint',
                    borderColor: 'brand.100',
                    avatarBg: 'brand.500'
                  },
                  {
                    quote: "Working with 30+ clients was chaotic before LiveWello. Now each client's health information is organized in their own profile, and I can collaborate with them seamlessly without them needing a paid subscription.",
                    name: "Lisa Martinez",
                    role: "Functional Medicine Practitioner",
                    bg: 'wellness.lavender',
                    borderColor: 'accent.100',
                    avatarBg: 'accent.400'
                  }
                ].map((testimonial, index) => (
                  <Box
                    key={index}
                    minW="100%"
                    px={{ base: 2, md: 4 }}
                  >
                    <Box
                      bg={testimonial.bg}
                      border="1px solid"
                      borderColor={testimonial.borderColor}
                      rounded="2xl"
                      p={{ base: 8, md: 12 }}
                      maxW="800px"
                      mx="auto"
                    >
                      <VStack gap={6} align="center" textAlign="center">
                        {/* Stars */}
                        <HStack justify="center">
                          {Array.from({ length: 5 }).map((_, i) => (
                            <FiStar
                              key={i}
                              size="20px"
                              color="var(--chakra-colors-yellow-400)"
                              fill="currentColor"
                            />
                          ))}
                        </HStack>

                        {/* Quote */}
                        <Text
                          fontSize={{ base: 'xl', md: '2xl' }}
                          color="fg"
                          fontStyle="italic"
                          lineHeight="tall"
                          maxW="600px"
                        >
                          "{testimonial.quote}"
                        </Text>

                        {/* Author */}
                        <HStack gap={3} pt={4}>
                          <Box
                            w={12}
                            h={12}
                            bg={testimonial.avatarBg}
                            rounded="full"
                          />
                          <VStack align="start" gap={0}>
                            <Text fontWeight="600" fontSize="md" color="fg">
                              {testimonial.name}
                            </Text>
                            <Text fontSize="sm" color="fg.muted">
                              {testimonial.role}
                            </Text>
                          </VStack>
                        </HStack>
                      </VStack>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Navigation Dots */}
            <HStack gap={3} justify="center" pt={4} flexWrap="wrap">
              {[0, 1, 2, 3, 4, 5, 6, 7].map((index) => (
                <Button
                  key={index}
                  onClick={() => setCurrentTestimonial(index)}
                  variant="ghost"
                  p={0}
                  minW="auto"
                  h="auto"
                  _hover={{ bg: 'transparent' }}
                  aria-label={`Go to testimonial ${index + 1}`}
                >
                  <Box
                    w={currentTestimonial === index ? '32px' : '8px'}
                    h="8px"
                    bg={currentTestimonial === index ? 'brand.500' : 'gray.300'}
                    rounded="full"
                    transition="all 0.3s ease"
                    _hover={{
                      bg: currentTestimonial === index ? 'brand.600' : 'gray.400'
                    }}
                  />
                </Button>
              ))}
            </HStack>
          </VStack>
        </Container>
      </Box>

      {/* Feedback */}
      <Box id="feedback" py={20} bg="bg">
        <Container maxW="5xl">
          <VStack gap={6} textAlign="center">
            <Heading size="2xl" color="fg" fontFamily="alteHaas" fontWeight="bold">{t('home.feedback_title')}</Heading>
            <Text fontSize="lg" color="fg.muted" maxW="700px">{t('home.feedback_subtitle')}</Text>
            <HStack gap={4} justify="center">
              {currentUser ? (
                <Button
                  size="lg"
                  variant="solid"
                  colorScheme="green"
                  color="white"
                  onClick={() => navigate('/feedback')}
                >
                  {t('home.feedback_cta', { defaultValue: 'Send Feedback' })}
                </Button>
              ) : (
                <>
                  <Button
                    asChild
                    size="lg"
                    variant="solid"
                    colorScheme="green"
                    color="white"
                  >
                    <RouterLink to="/login" state={{ from: { pathname: '/feedback' } }}>
                      {t('home.feedback_cta')}
                    </RouterLink>
                  </Button>
                  <Button asChild size="lg" variant="outline">
                    <RouterLink to="/login">{t('home.nav.login')}</RouterLink>
                  </Button>
                </>
              )}
            </HStack>
          </VStack>
        </Container>
      </Box>

      {/* FAQ Section */}
      <Box id="faq" py={20} bg="bg.muted">
        <Container maxW="4xl">
          <VStack gap={12}>
            <VStack gap={4} textAlign="center">
              <Heading size="2xl" color="fg" fontFamily="alteHaas" fontWeight="bold">
                {t('home.faq_title')}
              </Heading>
              <Text fontSize="lg" color="fg.muted">
                {t('home.faq_subtitle')}
              </Text>
            </VStack>

            <VStack gap={4} w="full">
              {[
                {
                  question: t('home.faq_1_question'),
                  answer: t('home.faq_1_answer'),
                },
                {
                  question: t('home.faq_2_question'),
                  answer: t('home.faq_2_answer'),
                },
                {
                  question: t('home.faq_3_question'),
                  answer: t('home.faq_3_answer'),
                },
                {
                  question: t('home.faq_4_question'),
                  answer: t('home.faq_4_answer'),
                },
              ].map((faq, index) => (
                <Box key={index} border="1px solid" borderColor="border" rounded="lg" p={6} bg="bg" w="full">
                  <VStack align="start" gap={3}>
                    <Text fontWeight="semibold" color="fg">
                      {faq.question}
                    </Text>
                    <Text color="fg.muted" lineHeight="tall">
                      {faq.answer}
                    </Text>
                  </VStack>
                </Box>
              ))}
            </VStack>
          </VStack>
        </Container>
      </Box>

      <PublicFooter />
    </Box>
  )
}

 